import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { diffWordsWithSpace } from "diff";
import type { AnalysisResult } from "@/components/ContractAnalyzer";

export type ContractRow = {
  id: string;
  label: string;
  tool_slug: string | null;
  created_at: string;
  updated_at: string;
  version_count: number;
  latest_risk: string | null;
  latest_risk_score: number | null;
};

export const listMyContracts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<ContractRow[]> => {
    const { userId } = context;

    const { data: contracts } = await supabaseAdmin
      .from("contracts")
      .select("id, label, tool_slug, created_at, updated_at")
      .eq("user_id", userId)
      .order("updated_at", { ascending: false });

    if (!contracts || contracts.length === 0) return [];

    const ids = contracts.map((c) => c.id as string);
    const { data: versions } = await supabaseAdmin
      .from("contract_versions")
      .select("id, contract_id, analysis_id, version_number, created_at")
      .in("contract_id", ids)
      .order("version_number", { ascending: false });

    const byContract = new Map<string, { count: number; latestAnalysisId: string | null }>();
    for (const v of versions ?? []) {
      const key = v.contract_id as string;
      const cur = byContract.get(key) ?? { count: 0, latestAnalysisId: null };
      cur.count += 1;
      if (cur.latestAnalysisId === null) cur.latestAnalysisId = v.analysis_id as string;
      byContract.set(key, cur);
    }

    const latestAnalysisIds = Array.from(byContract.values())
      .map((v) => v.latestAnalysisId)
      .filter((id): id is string => !!id);

    let analysisMap = new Map<string, { risk_level: string | null; risk_score: number | null }>();
    if (latestAnalysisIds.length > 0) {
      const { data: rows } = await supabaseAdmin
        .from("analyses")
        .select("id, risk_level, risk_score")
        .in("id", latestAnalysisIds);
      for (const r of rows ?? []) {
        analysisMap.set(r.id as string, {
          risk_level: (r.risk_level as string | null) ?? null,
          risk_score: (r.risk_score as number | null) ?? null,
        });
      }
    }

    return contracts.map((c) => {
      const meta = byContract.get(c.id as string);
      const latest = meta?.latestAnalysisId ? analysisMap.get(meta.latestAnalysisId) : null;
      return {
        id: c.id as string,
        label: c.label as string,
        tool_slug: (c.tool_slug as string | null) ?? null,
        created_at: c.created_at as string,
        updated_at: c.updated_at as string,
        version_count: meta?.count ?? 0,
        latest_risk: latest?.risk_level ?? null,
        latest_risk_score: latest?.risk_score ?? null,
      };
    });
  });

export type VersionRow = {
  id: string;
  version_number: number;
  created_at: string;
  note: string | null;
  analysis_id: string;
  risk_level: string | null;
  risk_score: number | null;
  title: string | null;
};

export const getContractWithVersions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ contractId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { userId } = context;

    const { data: contract } = await supabaseAdmin
      .from("contracts")
      .select("id, label, tool_slug, created_at, updated_at")
      .eq("user_id", userId)
      .eq("id", data.contractId)
      .maybeSingle();

    if (!contract) return null;

    const { data: versions } = await supabaseAdmin
      .from("contract_versions")
      .select("id, version_number, created_at, note, analysis_id")
      .eq("contract_id", contract.id as string)
      .order("version_number", { ascending: false });

    const analysisIds = (versions ?? []).map((v) => v.analysis_id as string);
    let analysisMap = new Map<
      string,
      { risk_level: string | null; risk_score: number | null; title: string | null }
    >();
    if (analysisIds.length > 0) {
      const { data: rows } = await supabaseAdmin
        .from("analyses")
        .select("id, risk_level, risk_score, title")
        .in("id", analysisIds);
      for (const r of rows ?? []) {
        analysisMap.set(r.id as string, {
          risk_level: (r.risk_level as string | null) ?? null,
          risk_score: (r.risk_score as number | null) ?? null,
          title: (r.title as string | null) ?? null,
        });
      }
    }

    const versionRows: VersionRow[] = (versions ?? []).map((v) => {
      const m = analysisMap.get(v.analysis_id as string);
      return {
        id: v.id as string,
        version_number: v.version_number as number,
        created_at: v.created_at as string,
        note: (v.note as string | null) ?? null,
        analysis_id: v.analysis_id as string,
        risk_level: m?.risk_level ?? null,
        risk_score: m?.risk_score ?? null,
        title: m?.title ?? null,
      };
    });

    return {
      contract: {
        id: contract.id as string,
        label: contract.label as string,
        tool_slug: (contract.tool_slug as string | null) ?? null,
        created_at: contract.created_at as string,
        updated_at: contract.updated_at as string,
      },
      versions: versionRows,
    };
  });

const saveInput = z.object({
  analysisId: z.string().uuid(),
  contractId: z.string().uuid().optional(),
  label: z.string().min(1).max(120).optional(),
  toolSlug: z.string().max(64).optional(),
  extractedText: z.string().max(80000).optional(),
  note: z.string().max(280).optional(),
});

export const saveAnalysisAsVersion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => saveInput.parse(input))
  .handler(async ({ data, context }) => {
    const { userId } = context;

    // Confirm the analysis belongs to the user
    const { data: analysis } = await supabaseAdmin
      .from("analyses")
      .select("id, user_id, tool_slug, title")
      .eq("id", data.analysisId)
      .maybeSingle();

    if (!analysis || analysis.user_id !== userId) {
      return { ok: false as const, reason: "not_found" as const };
    }

    let contractId = data.contractId ?? null;

    if (contractId) {
      const { data: c } = await supabaseAdmin
        .from("contracts")
        .select("id")
        .eq("id", contractId)
        .eq("user_id", userId)
        .maybeSingle();
      if (!c) return { ok: false as const, reason: "not_found" as const };
    } else {
      const label = data.label ?? (analysis.title as string | null) ?? "Untitled contract";
      const { data: created, error } = await supabaseAdmin
        .from("contracts")
        .insert([
          {
            user_id: userId,
            label,
            tool_slug: data.toolSlug ?? (analysis.tool_slug as string | null) ?? null,
          },
        ])
        .select("id")
        .single();
      if (error || !created) {
        return { ok: false as const, reason: "create_failed" as const };
      }
      contractId = created.id as string;
    }

    const { data: version, error: vErr } = await supabaseAdmin
      .from("contract_versions")
      .insert([
        {
          contract_id: contractId,
          analysis_id: data.analysisId,
          version_number: 0, // trigger reassigns
          extracted_text: data.extractedText ?? null,
          note: data.note ?? null,
        },
      ])
      .select("id, version_number")
      .single();

    if (vErr || !version) {
      return { ok: false as const, reason: "create_failed" as const };
    }

    return {
      ok: true as const,
      contractId,
      versionId: version.id as string,
      versionNumber: version.version_number as number,
    };
  });

export const deleteContract = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ contractId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    await supabaseAdmin.from("contracts").delete().eq("user_id", userId).eq("id", data.contractId);
    return { ok: true };
  });

// ---------- Diff ----------

export type ClauseFieldDiff = {
  field: "what" | "why" | "suggestion";
  parts: Array<{ value: string; added?: boolean; removed?: boolean }>;
};

export type ChangedClause = {
  title: string;
  fields: ClauseFieldDiff[];
  severityFrom: string | null;
  severityTo: string | null;
};

export type ClauseDiff = {
  added: Array<{ title: string; what: string; severity: string }>;
  removed: Array<{ title: string; what: string; severity: string }>;
  changed: ChangedClause[];
  unchangedCount: number;
  riskScoreDelta: number;
  redFlagDelta: { added: string[]; removed: string[] };
};

export type VersionSummary = {
  id: string;
  versionNumber: number;
  createdAt: string;
  riskLevel: string | null;
  riskScore: number | null;
  title: string | null;
  result: AnalysisResult | null;
};

function fieldDiff(a: string, b: string): ClauseFieldDiff["parts"] {
  if (a === b) return [{ value: b }];
  const parts = diffWordsWithSpace(a ?? "", b ?? "");
  return parts.map((p) => ({
    value: p.value,
    added: p.added,
    removed: p.removed,
  }));
}

function buildDiff(a: AnalysisResult | null, b: AnalysisResult | null): ClauseDiff {
  const aClauses = a?.clauseExplanations ?? [];
  const bClauses = b?.clauseExplanations ?? [];
  const aMap = new Map(aClauses.map((c) => [c.title.trim().toLowerCase(), c]));
  const bMap = new Map(bClauses.map((c) => [c.title.trim().toLowerCase(), c]));

  const added: ClauseDiff["added"] = [];
  const removed: ClauseDiff["removed"] = [];
  const changed: ChangedClause[] = [];
  let unchangedCount = 0;

  for (const [key, bc] of bMap) {
    const ac = aMap.get(key);
    if (!ac) {
      added.push({ title: bc.title, what: bc.what, severity: bc.severity });
      continue;
    }
    const fields: ClauseFieldDiff[] = [];
    if (ac.what !== bc.what) fields.push({ field: "what", parts: fieldDiff(ac.what, bc.what) });
    if (ac.why !== bc.why) fields.push({ field: "why", parts: fieldDiff(ac.why, bc.why) });
    if (ac.suggestion !== bc.suggestion)
      fields.push({ field: "suggestion", parts: fieldDiff(ac.suggestion, bc.suggestion) });
    if (fields.length === 0 && ac.severity === bc.severity) {
      unchangedCount += 1;
    } else {
      changed.push({
        title: bc.title,
        fields,
        severityFrom: ac.severity,
        severityTo: bc.severity,
      });
    }
  }
  for (const [key, ac] of aMap) {
    if (!bMap.has(key)) {
      removed.push({ title: ac.title, what: ac.what, severity: ac.severity });
    }
  }

  const aFlags = new Set(a?.redFlags ?? []);
  const bFlags = new Set(b?.redFlags ?? []);
  const flagsAdded = [...bFlags].filter((f) => !aFlags.has(f));
  const flagsRemoved = [...aFlags].filter((f) => !bFlags.has(f));

  return {
    added,
    removed,
    changed,
    unchangedCount,
    riskScoreDelta: (b?.riskScore ?? 0) - (a?.riskScore ?? 0),
    redFlagDelta: { added: flagsAdded, removed: flagsRemoved },
  };
}

export const getVersionDiff = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        versionAId: z.string().uuid(),
        versionBId: z.string().uuid(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { userId } = context;

    const { data: vRows } = await supabaseAdmin
      .from("contract_versions")
      .select("id, contract_id, analysis_id, version_number, created_at")
      .in("id", [data.versionAId, data.versionBId]);

    if (!vRows || vRows.length !== 2) {
      throw new Error("Versions not found");
    }
    // Verify both versions belong to a contract owned by the user
    const contractIds = Array.from(new Set(vRows.map((v) => v.contract_id as string)));
    const { data: cRows } = await supabaseAdmin
      .from("contracts")
      .select("id, user_id")
      .in("id", contractIds);
    for (const c of cRows ?? []) {
      if ((c.user_id as string) !== userId) throw new Error("Forbidden");
    }

    const analysisIds = vRows.map((v) => v.analysis_id as string);
    const { data: aRows } = await supabaseAdmin
      .from("analyses")
      .select("id, result, risk_level, risk_score, title, created_at")
      .in("id", analysisIds);

    const aMap = new Map<string, any>((aRows ?? []).map((r) => [r.id as string, r]));

    const vA = vRows.find((v) => v.id === data.versionAId)!;
    const vB = vRows.find((v) => v.id === data.versionBId)!;
    const aA = aMap.get(vA.analysis_id as string);
    const aB = aMap.get(vB.analysis_id as string);

    const summarize = (vRow: typeof vA, analysis: any | undefined): VersionSummary => ({
      id: vRow.id as string,
      versionNumber: vRow.version_number as number,
      createdAt: vRow.created_at as string,
      riskLevel: (analysis?.risk_level as string | null) ?? null,
      riskScore: (analysis?.risk_score as number | null) ?? null,
      title: (analysis?.title as string | null) ?? null,
      result: (analysis?.result as AnalysisResult | null) ?? null,
    });

    return {
      a: summarize(vA, aA),
      b: summarize(vB, aB),
      diff: buildDiff(
        (aA?.result as AnalysisResult | null) ?? null,
        (aB?.result as AnalysisResult | null) ?? null,
      ),
    };
  });
